import format from 'date-fns/format'
import formatDistance from 'date-fns/formatDistance'
import differenceInYears from 'date-fns/differenceInYears'
import parseISO from 'date-fns/parseISO'
import isNaN from 'lodash/isNaN'
import inRange from 'lodash/inRange'
import type { DayOfWeek } from './models/enums'

export const displayDate = (date?: string | Date): string | null => date ? format(new Date(date), 'M/d/yyyy') : null
export const displayDateTime = (date?: string | Date): string | null => date ? format(new Date(date), 'MM/dd/yyy h:mm a') : null
export const displayNotificationTimeAgo = (createdOn: Date | string): string => {
    const value = typeof createdOn === 'string' ? new Date(createdOn) : createdOn
    const timestamp = value.getTime()
    if (isNaN(timestamp)) return 'Now'

    const diff = Date.now() - timestamp
    if (diff < 0) return 'Now'

    const totalMinutes = Math.floor(diff / (1000 * 60))
    if (totalMinutes < 60) return 'Now'

    const totalHours = Math.floor(totalMinutes / 60)

    if (totalHours < 24) {
        const hours = Math.max(1, totalHours)
        return `${hours} hr${hours === 1 ? '' : 's'} ago`
    }

    const days = Math.floor(totalHours / 24)
    return `${days} day${days === 1 ? '' : 's'} ago`
}
export const parseCurrencyToFloat = (value?: string | number): number | null => value && !isNaN(value) ? parseFloat(value.toString().replace(/\$|,/g, '')) : null

const formatter = new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2
})

export const parseFloatToCurrency = (value?: number | string): string => value && !isNaN(value) ? formatter.format(parseFloat(value.toString())) : ''

const ageRanges: Record<string, [number, number]> = {
    'Infant': [-Infinity, 1],
    'Toddler': [2, 3],
    'Preschool': [4, 5],
    'School Age': [6, 12],
    'Adolescent': [13, 17],
    'Young Adult': [18, 25],
    'Middle Adult': [26, 55],
    'Older Adult': [56, Infinity]
}

export const displayAge = (date?: string | Date): number | null => {
    if (date && date === 'string') {
        date = parseISO(date)
        if (isNaN(date))
            date = new Date(date)
    }

    return date ? differenceInYears(new Date(), new Date(date)) : null
}

export const displayAgeRange = (date?: string | Date): string | undefined => {
    if (date === null) return ''

    const age = displayAge(date)

    return age
        ? Object.keys(ageRanges).find(k => inRange(age, ageRanges[k][0], ageRanges[k][1]))
        : undefined
}

export const displayCountdown = (futureDate?: Date | string): string => {
    if (!futureDate) return 'NA'

    const fd = new Date(futureDate)
    fd.setDate(fd.getDate() + 1) //Add a day, since end date is the start of the expiry day
    const cd = new Date()

    if (cd > fd) return 'Expired'

    return formatDistance(fd, cd)
}

export const displayPhone = (phone?: string): string => {
    if (!phone) return ''

    return phone.replace(/\(?(\d{3})[) -.]*(\d{3})[-.]?(\d{4})/, '$1-$2-$3')
}

export const displayResponseStatus = (response: string): string => response === 'NoInterest' ? 'Not Interested' : 'Interested'

export const displayZipCode = (zip?: string): string => {
    if (!zip) return ''
    const digits = zip.replace(/\D/g, '')
    return digits.length >= 5 ? digits.slice(0, 5) : digits
}

const sortedDays: DayOfWeek[] = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday']

export const sortDays = (dayList: DayOfWeek[]): DayOfWeek[] =>
    dayList.sort((a, b) => sortedDays.indexOf(a) > sortedDays.indexOf(b) ? 1 : -1)

export const displayDayList = (dayList?: DayOfWeek[]): string => {
    if (!dayList) return ''
    if (dayList.length === 1) return dayList[0]
    if (dayList.length === 2) return `${dayList[0]}/${dayList[1]}`
    if (dayList.length === 7) return 'Every Day'

    return dayList.map(day => {
        switch (day) {
            case 'Sunday': return 'Sun'
            case 'Monday': return 'Mon'
            case 'Tuesday': return 'Tue'
            case 'Wednesday': return 'Wed'
            case 'Thursday': return 'Thu'
            case 'Friday': return 'Fri'
            case 'Saturday': return 'Sat'
            default: return ''
        }
    }).join('/')
}

export const ValidAttachmentExtensions = '.jpg, .pdf, .doc, .docx'

// Returns the root registrable domain from the current hostname so that cookies
// set here are readable across subdomains (e.g. Provider Portal on a sibling
// subdomain).  Returns undefined for host-only environments where setting a
// Domain cookie attribute is invalid:
//   - Single-label hosts  (localhost)
//   - IPv4 addresses      (127.0.0.1)
//   - IPv6 addresses      (::1, [::1])
// Examples:
//   providersourcing.caresource.com   → "caresource.com"
//   local-providersourcing.caresource.com → "caresource.com"
//   localhost                         → undefined
//   127.0.0.1                         → undefined
export const getRootDomain = (): string | undefined => {
    const hostname = window.location.hostname
    const parts = hostname.split('.')
    const isIPv4 = parts.length === 4 && parts.every(p => /^\d+$/.test(p))
    const isIPv6 = hostname.includes(':') || hostname.startsWith('[')
    if (isIPv4 || isIPv6 || parts.length < 2) return undefined
    return parts.slice(-2).join('.')
}

export const isHAPLoginIntent = (value?: string | null): boolean => {
    if (!value) return false

    try {
        return decodeURIComponent(value.replace(/\+/g, ' ')) === 'HAP'
    } catch {
        return false
    }
}

export const isValidHAPLink = (value?: string): boolean => {
    const link = value?.trim()
    if (!link || link.includes('#{')) return false

    // Require an absolute HTTPS URL (avoid `new URL(...)` which is not supported in IE9).
    if (!/^https:\/\//i.test(link)) return false

    try {
        // Use an anchor element for parsing (works in IE9 and Jest/jsdom).
        if (typeof document === 'undefined') {
            return /^https:\/\/([a-z0-9-]+\.)*hap\.org(\/|$)/i.test(link)
        }

        const a = document.createElement('a')
        a.href = link

        const protocol = (a.protocol || '').toLowerCase()
        const hostname = (a.hostname || '').toLowerCase()

        return protocol === 'https:' && (hostname === 'hap.org' || hostname.endsWith('.hap.org'))
    } catch {
        return false
    }
}
